'use client';

import { ComponentPropsWithRef, ComponentRef, forwardRef, ReactNode, useState } from 'react';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

type InputProps = ComponentPropsWithRef<'input'> & {
  error?: boolean;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
};

export const Input = forwardRef<ComponentRef<'input'>, InputProps>(
  ({ className, error = false, icon, iconPosition = 'right', type = 'text', ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const [hasValue, setHasValue] = useState(Boolean(props.value || props.defaultValue));
    const isPassword = type === 'password';
    const showPasswordToggle = isPassword && hasValue;
    const inputType = isPassword ? 'text' : type;
    const inputPadding =
      icon || showPasswordToggle ? (iconPosition === 'left' ? 'ps-12 pe-4' : 'ps-4 pe-12') : 'px-4';

    return (
      <div className={cn('relative mb-2 w-full', className)}>
        <input
          className={cn(
            'peer border-border bg-background-panel placeholder:text-text-subtle enabled:hover:border-primary enabled:focus:border-border-muted w-full rounded-[3px] border border-solid py-2.5 text-base outline-hidden disabled:cursor-not-allowed disabled:opacity-60',
            inputPadding,
            isPassword && !showPassword && '[-webkit-text-security:disc]',
            error &&
              'enabled:focus-visible:ring-error/20 border-error enabled:hover:border-error enabled:focus-visible:border-error disabled:border-border-muted',
          )}
          ref={ref}
          type={inputType}
          {...props}
          onChange={(e) => {
            setHasValue(e.target.value.length > 0);
            props.onChange?.(e);
          }}
        />
        {showPasswordToggle && (
          <button
            type='button'
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className={cn(
              'text-text-muted hover:text-text peer-disabled:text-text-muted absolute top-0 flex h-full cursor-pointer items-center peer-disabled:pointer-events-none',
              iconPosition === 'left' ? 'start-4' : 'end-4',
            )}
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        )}
        {!showPasswordToggle && Boolean(error || icon) && (
          <span
            aria-hidden='true'
            className={cn(
              'peer-disabled:text-text-muted pointer-events-none absolute top-0 flex h-full items-center',
              iconPosition === 'left' ? 'start-4' : 'end-4',
              error && 'text-error',
            )}
          >
            {icon ?? (error && <AlertCircle />)}
          </span>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
